/**
 * The Ledger layers on tokens: dialog, popover, hover card, menu, toast and
 * tabs. The drawer (the Ledger Sheet) is `./drawer.ts`.
 *
 * Every layer is opaque: surface colour, 1px border, a token shadow, no blur
 * and no translucency (DEVIATIONS.md, D-08). Stacking comes from the layer
 * manager (`src/theme/layerStack.ts`): the `--<part>-z-index` variables Chakra
 * reads are pointed at its bands here, so no recipe names a number.
 *
 * Spec: docs/design-system/DESIGN-SYSTEM.md, "Components 4: layers and
 * feedback"; specs/design-system/ledger-layers.feature.
 */
import { defineSlotRecipe } from "@chakra-ui/react";
import { compactPressArea, focusRing, listOption } from "./shared";

/** One layer surface: opaque, bordered, a token shadow, no blur. */
const layerSurface = (shadow: "--shadow-2" | "--shadow-3") =>
  ({
    // `bg`, the key Chakra's own recipes use, so the deep merge replaces
    // their paint instead of adding a second background beside it.
    bg: "var(--color-surface)",
    color: "var(--color-fg)",
    border: "1px solid",
    borderColor: "var(--color-border)",
    boxShadow: `var(${shadow})`,
    backdropFilter: "none",
  }) as const;

/** A width in multiples of `--space-8` (40px): 12 is 480, 16 is 640, 20 is 800. */
const spaceWidth = (steps: number) => `calc(var(--space-8) * ${steps})`;

/**
 * Dialog: sm 480 / md 640 / lg 800, radius 6, shadow-3, the scrim behind it,
 * a 16px semibold title, the body scrolling, the footer's actions right with
 * the primary last. `xl`, `5xl`, `6xl`, `cover` and `full` keep Chakra's own
 * widths for the editors that use them (DEVIATIONS.md, L-04).
 */
export const dialogSlotRecipe = defineSlotRecipe({
  slots: [
    "trigger",
    "backdrop",
    "positioner",
    "content",
    "title",
    "description",
    "closeTrigger",
    "header",
    "body",
    "footer",
  ],
  base: {
    backdrop: {
      "--dialog-z-index": "zIndex.modal",
      background: "var(--color-scrim)",
      backdropFilter: "none",
      _open: { animationDuration: "var(--dur-3)" },
      _closed: { animationDuration: "var(--dur-2)" },
    },
    positioner: {
      "--dialog-z-index": "zIndex.modal",
    },
    content: {
      ...layerSurface("--shadow-3"),
      "--dialog-z-index": "zIndex.modal",
      borderRadius: "var(--radius-4)",
      fontSize: "var(--text-13)",
      textStyle: "none",
      // Never wider than the screen less a 16px gutter each side.
      width: "calc(100% - var(--space-7))",
      _open: {
        animationDuration: "var(--dur-3)",
        animationTimingFunction: "var(--ease-out)",
      },
      _closed: {
        animationDuration: "var(--dur-2)",
        animationTimingFunction: "var(--ease-in)",
      },
    },
    header: {
      alignItems: "center",
      gap: "var(--space-2)",
      paddingInline: "var(--space-5)",
      // Room on the right for the close button, which is placed absolutely.
      paddingInlineEnd: "calc(var(--space-5) + var(--hit-min))",
      paddingTop: "var(--space-4)",
      paddingBottom: "var(--space-3)",
    },
    title: {
      fontSize: "var(--text-16)",
      fontWeight: "var(--weight-semibold)",
      lineHeight: "var(--leading-tight)",
      textStyle: "none",
    },
    description: {
      color: "var(--color-fg-2)",
      fontSize: "var(--text-13)",
    },
    body: {
      paddingInline: "var(--space-5)",
      paddingTop: "0",
      paddingBottom: "var(--space-4)",
      fontSize: "var(--text-14)",
      fontWeight: "var(--weight-regular)",
      lineHeight: "var(--leading-normal)",
    },
    footer: {
      gap: "var(--space-2)",
      paddingInline: "var(--space-5)",
      paddingTop: "var(--space-3)",
      paddingBottom: "var(--space-4)",
      borderTop: "1px solid",
      borderColor: "var(--color-border-2)",
    },
    closeTrigger: {
      top: "var(--space-3)",
      insetEnd: "var(--space-3)",
    },
  },
  variants: {
    size: {
      xs: { content: { maxWidth: spaceWidth(12) } },
      sm: { content: { maxWidth: spaceWidth(12) } },
      md: { content: { maxWidth: spaceWidth(16) } },
      lg: { content: { maxWidth: spaceWidth(20) } },
      "5xl": { content: { maxWidth: "5xl" } },
      "6xl": { content: { maxWidth: "6xl" } },
    },
  },
});

/**
 * Popover: surface, 1px border, radius 4, shadow-2, 140ms in. Positioned and
 * flipped by Zag, never wider than the room on its side.
 */
export const popoverSlotRecipe = defineSlotRecipe({
  slots: [
    "arrow",
    "arrowTip",
    "anchor",
    "trigger",
    "indicator",
    "positioner",
    "content",
    "title",
    "description",
    "closeTrigger",
    "header",
    "body",
    "footer",
  ],
  base: {
    content: {
      ...layerSurface("--shadow-2"),
      "--popover-bg": "var(--color-surface)",
      "--popover-z-index": "zIndex.popover",
      borderRadius: "var(--radius-3)",
      fontSize: "var(--text-13)",
      textStyle: "none",
      maxWidth: "min(var(--popover-size), var(--available-width, 100dvw))",
      _open: { animationDuration: "var(--dur-2)" },
      _closed: { animationDuration: "var(--dur-1)" },
    },
    arrow: {
      "--arrow-background": "var(--color-surface)",
    },
    arrowTip: {
      borderColor: "var(--color-border)",
    },
    title: {
      fontSize: "var(--text-14)",
      fontWeight: "var(--weight-semibold)",
    },
  },
});

/** Hover card: the popover surface, opened on hover. */
export const hoverCardSlotRecipe = defineSlotRecipe({
  slots: ["arrow", "arrowTip", "trigger", "positioner", "content"],
  base: {
    content: {
      ...layerSurface("--shadow-2"),
      "--hovercard-bg": "var(--color-surface)",
      "--hover-card-z-index": "zIndex.popover",
      borderRadius: "var(--radius-3)",
      fontSize: "var(--text-13)",
      textStyle: "none",
    },
    arrow: {
      "--arrow-background": "var(--color-surface)",
    },
  },
});

/**
 * Menu: the popover surface with `role="menu"`; 28px items, 32px below 1024
 * so a finger can hit them; an icon, a label and a key hint; hairline
 * separators. A destructive item (`ui/menu.tsx`, `destructive`) reads in err
 * and goes last.
 */
export const menuSlotRecipe = defineSlotRecipe({
  slots: [
    "arrow",
    "arrowTip",
    "content",
    "contextTrigger",
    "indicator",
    "item",
    "itemGroup",
    "itemGroupLabel",
    "itemIndicator",
    "itemText",
    "positioner",
    "separator",
    "trigger",
    "triggerItem",
    "itemCommand",
  ],
  base: {
    content: {
      ...layerSurface("--shadow-2"),
      "--menu-bg": "var(--color-surface)",
      "--menu-z-index": "zIndex.popover",
      borderRadius: "var(--radius-3)",
      padding: "var(--space-1)",
      minWidth: "calc(var(--space-8) * 4)",
      _open: { animationDuration: "var(--dur-2)" },
      _closed: { animationDuration: "var(--dur-1)" },
    },
    item: {
      ...listOption,
      minHeight: { base: "var(--control-lg)", lg: "var(--control-md)" },
      gap: "var(--space-2)",
      cursor: "pointer",
      _icon: {
        width: "var(--icon-sm)",
        height: "var(--icon-sm)",
        flexShrink: 0,
      },
      "&[data-destructive]": {
        color: "var(--color-err)",
        _highlighted: {
          background: "var(--color-err-soft)",
          color: "var(--color-err-soft-fg)",
        },
      },
      "&[data-type]": { paddingInlineStart: "var(--space-7)" },
    },
    itemText: { flex: "1", minWidth: 0 },
    itemCommand: {
      opacity: 1,
      color: "var(--color-fg-3)",
      fontFamily: "var(--font-mono)",
      fontSize: "var(--text-11)",
      letterSpacing: "normal",
      marginStart: "auto",
      paddingStart: "var(--space-4)",
      // Muted text never sits on the hover surface: it steps up to fg-2.
      "[data-highlighted] &": { color: "var(--color-fg-2)" },
    },
    itemGroupLabel: {
      paddingInline: "var(--space-2)",
      paddingBlock: "var(--space-1)",
      fontSize: "var(--text-12)",
      fontWeight: "var(--weight-medium)",
      color: "var(--color-fg-3)",
      textStyle: "none",
    },
    separator: {
      background: "var(--color-border-2)",
      marginBlock: "var(--space-1)",
      marginInline: "calc(var(--space-1) * -1)",
    },
    arrow: {
      "--arrow-background": "var(--color-surface)",
    },
  },
  variants: {
    size: {
      sm: {
        content: { padding: "var(--space-1)" },
        item: {
          fontSize: "var(--text-13)",
          paddingBlock: "0",
          paddingInline: "var(--space-2)",
          textStyle: "none",
        },
      },
      md: {
        content: { padding: "var(--space-1)" },
        item: {
          fontSize: "var(--text-13)",
          paddingBlock: "0",
          paddingInline: "var(--space-2)",
          textStyle: "none",
        },
      },
    },
  },
});

/**
 * Toast: the surface, 1px border, radius 4, shadow-2 in both themes; the
 * status in a small icon in its own colour and in the title's words. The
 * Ledger's inverse toast was not used: on the inverse surface the err and info
 * icons fall under 3:1 (DEVIATIONS.md, L-07). `components/ui/toaster.tsx` sets
 * the placement, the 4s default and the persistent error.
 */
const toastStatusPanel = {
  bg: "var(--color-surface)",
  color: "var(--color-fg)",
  borderColor: "var(--color-border)",
  "--toast-trigger-bg": "var(--color-surface-3)",
  "--toast-border-color": "var(--color-border)",
} as const;

export const toastSlotRecipe = defineSlotRecipe({
  slots: [
    "root",
    "title",
    "description",
    "indicator",
    "closeTrigger",
    "actionTrigger",
  ],
  base: {
    root: {
      ...layerSurface("--shadow-2"),
      borderRadius: "var(--radius-3)",
      alignItems: "flex-start",
      gap: "var(--space-2)",
      paddingBlock: "var(--space-3)",
      paddingInlineStart: "var(--space-3)",
      paddingInlineEnd: "var(--space-2)",
      ...toastStatusPanel,
      // Chakra fills the status toasts; the Ledger toast stays a surface and
      // carries the status in its icon. Same selectors as Chakra's fills, so
      // these replace them in the merge.
      "&[data-type=error]": toastStatusPanel,
      "&[data-type=warning]": toastStatusPanel,
      "&[data-type=success]": toastStatusPanel,
      "&[data-type=info]": toastStatusPanel,
      "&[data-type=loading]": toastStatusPanel,
    },
    title: {
      fontSize: "var(--text-13)",
      fontWeight: "var(--weight-medium)",
      color: "var(--color-fg)",
      textStyle: "none",
      marginEnd: "0",
    },
    description: {
      fontSize: "var(--text-13)",
      color: "var(--color-fg-2)",
      opacity: 1,
      textStyle: "none",
    },
    actionTrigger: {
      fontSize: "var(--text-13)",
      fontWeight: "var(--weight-medium)",
      color: "var(--color-link)",
      height: "var(--control-md)",
      paddingInline: "var(--space-2)",
      borderRadius: "var(--radius-2)",
      _focusVisible: focusRing,
    },
    closeTrigger: {
      color: "var(--color-fg-3)",
      _hover: { color: "var(--color-fg)" },
      _focusVisible: focusRing,
    },
  },
});

/**
 * Tabs: 28px triggers, `--text-13` 500, the selected tab underlined in the
 * accent, roving tabindex and arrows/Home/End from Zag, wrapping rather than
 * overflowing on a narrow screen. The other Chakra variants keep their shapes
 * on tokens.
 */
export const tabsSlotRecipe = defineSlotRecipe({
  slots: ["root", "trigger", "list", "content", "contentGroup", "indicator"],
  base: {
    root: {
      "--tabs-height": "var(--control-md)",
      "--tabs-trigger-radius": "var(--radius-2)",
      "--tabs-indicator-bg": "var(--color-surface)",
      "--tabs-indicator-shadow": "none",
      "--tabs-content-padding": "var(--space-4)",
    },
    list: {
      flexWrap: "wrap",
      maxWidth: "100%",
    },
    trigger: {
      fontSize: "var(--text-13)",
      fontWeight: "var(--weight-medium)",
      textStyle: "none",
      color: "var(--color-fg-2)",
      height: "var(--tabs-height)",
      paddingBlock: "0",
      paddingInline: "var(--space-3)",
      gap: "var(--space-2)",
      whiteSpace: "nowrap",
      _hover: { color: "var(--color-fg)" },
      _focusVisible: { ...focusRing, zIndex: "auto", outline: "none" },
      _disabled: { color: "var(--color-fg-disabled)", opacity: 1 },
      _icon: { width: "var(--icon-sm)", height: "var(--icon-sm)" },
      // A 28px tab presses at 32px below 1024 (P-20). The line variant's
      // accent underline is `::before`, so `::after` is free.
      ...compactPressArea,
    },
  },
  variants: {
    size: {
      sm: {
        root: { "--tabs-height": "var(--control-md)" },
        trigger: {
          fontSize: "var(--text-13)",
          paddingInline: "var(--space-3)",
        },
      },
      md: {
        root: { "--tabs-height": "var(--control-md)" },
        trigger: {
          fontSize: "var(--text-13)",
          paddingInline: "var(--space-3)",
        },
      },
      lg: {
        root: { "--tabs-height": "var(--control-lg)" },
        trigger: {
          fontSize: "var(--text-13)",
          paddingInline: "var(--space-4)",
        },
      },
    },
    variant: {
      line: {
        list: {
          borderColor: "var(--color-border-2)",
        },
        trigger: {
          _selected: {
            color: "var(--color-fg)",
            _horizontal: { "--indicator-color": "var(--color-accent)" },
            _vertical: { "--indicator-color": "var(--color-accent)" },
          },
        },
      },
      subtle: {
        list: { borderBottom: "none" },
        trigger: {
          borderRadius: "var(--radius-2)",
          _selected: {
            background: "var(--color-surface-selected)",
            color: "var(--color-accent-soft-fg)",
          },
        },
      },
      enclosed: {
        list: {
          background: "var(--color-surface-2)",
          borderRadius: "var(--radius-3)",
          gap: "var(--space-1)",
          padding: "var(--space-1)",
        },
        trigger: {
          borderRadius: "var(--radius-2)",
          _selected: {
            background: "var(--color-surface)",
            color: "var(--color-fg)",
            boxShadow: "none",
            outline: "1px solid var(--color-border)",
          },
        },
      },
      outline: {
        root: { "--tabs-trigger-radius": "var(--radius-2)" },
        list: {
          _horizontal: { _before: { bottom: "1px", left: "0" } },
        },
      },
    },
  },
});
